import React, { useContext, useEffect, useState } from "react"
import tw from "twin.macro"
import { StandardEquipmentSectionProps } from "./StandardEquipment.d"
import { Model } from "../../../../global"
import { parseDisclaimerBlocks } from "../../../../helpers"
import { DisclaimersContext } from "../../../../contexts/Disclaimers"
import { toggleDisclaimersModal } from "../../../../contexts/Disclaimers/actions"
import { Accordion } from "../../../atoms/Accordion"
import { SeriesPageContext } from "../../../../contexts/SeriesPage"
import ModelListItem from "../ModelList/ModelListItem"
import { LanguageContext } from "../../../../contexts/Language"
import AliceCarousel from "react-alice-carousel"
import Icon from "../../../atoms/Icon"
import useDrag from "../../../../hooks/useDrag"

/**
 *
 * @author Cody
 * @summary
 *
 *
 */

const StandardEquipmentSection: React.FC<StandardEquipmentSectionProps> = ({
  heading,
}) => {
  const [state, modalDispatch] = useContext(DisclaimersContext)
  const [{ models }] = useContext(SeriesPageContext)
  const { language, _ } = useContext(LanguageContext)
  const [selectedModelGroup, setSelectedModelGroup] = useState<any>(null)
  const [standardEquipment, setStandardEquipment] = useState<any>(null)

  useEffect(() => {
    models && setSelectedModelGroup(models[0])
  }, [models])

  useEffect(() => {
    if (selectedModelGroup) {
      const newStandardEquipData =
        language === "es"
          ? selectedModelGroup[0].model.standardEquipment.es
          : selectedModelGroup[0].model.standardEquipment.en
      const featuredStandardEquip = newStandardEquipData?.filter(
        (standardEquip: any) => standardEquip?.type === "features",
      )
      setStandardEquipment(featuredStandardEquip)
    }
  }, [selectedModelGroup])

  const items =
    selectedModelGroup &&
    models?.map((modelGroup: Model[], i: number) => {
      return (
        <React.Fragment key={`model-list-item-${i}`}>
          <ModelListItem
            onSelect={() => {
              setSelectedModelGroup(
                models.filter(
                  (e: Model[]) => e[0]?.model?.id === modelGroup[0]?.model?.id,
                )[0],
              )
            }}
            active={
              modelGroup[0]?.model?.id === selectedModelGroup[0]?.model?.id
            }
            modelGroup={modelGroup}
            showConfigurations={true}
            analytics-id={`model selector:model list:${i + 1}`}
          />
        </React.Fragment>
      )
    })

  const { handleMouseUp } = useDrag()

  const responsive = {
    0: { items: 1 },
    600: { items: 2 },
    859: { items: 3 },
    1023: { items: 3 },
    1100: { items: 4 },
    1440: { items: 5 },
    1600: { items: 6 },
    1919: { items: 7 },
  }
  const renderPrevButton = ({ isDisabled }: { isDisabled?: boolean }) => {
    return (
      <button
        css={[
          tw`absolute -left-6 top-[45%] -translate-y-1/2 z-50 flex justify-center items-center cursor-pointer w-16 h-10 bg-white rounded-full transition-all hover:bg-gray-400 focus-visible:(bg-gray-400)`,
          tw`lg:(-left-10)`,
          isDisabled ? tw`hidden` : tw`flex`,
        ]}
      >
        <Icon.Chevron direction="left" color="black" css={[tw`h-5`]} />
      </button>
    )
  }

  const renderNextButton = ({ isDisabled }: { isDisabled?: boolean }) => {
    return (
      <button
        css={[
          tw`absolute -right-6 top-[45%] -translate-y-1/2 z-50 flex justify-center items-center cursor-pointer w-16 h-10 bg-white rounded-full transition-all hover:(bg-gray-400) focus-visible:(bg-gray-400)`,
          tw`lg:(-right-10)`,
          isDisabled ? tw`hidden` : tw`flex`,
        ]}
      >
        <Icon.Chevron direction="right" color="black" css={[tw`h-5`]} />
      </button>
    )
  }
  return (
    <section
      css={[
        tw`px-0 w-full bg-gradient-to-b bg-gray-100 to-white pt-12 mb-6`,
        tw`md:(mx-auto)`,
      ]}
      aria-label="Standard Equipment Section"
    >
      {heading && (
        <div
          css={[
            tw`justify-self-center text-3xl font-light tracking-widest mb-10 text-left px-6`,
            tw`lg:(text-5xl mx-auto px-12)`,
            tw`2xl:(px-20)`,
          ]}
        >
          {parseDisclaimerBlocks(heading, selection =>
            modalDispatch(toggleDisclaimersModal(selection)),
          )}
        </div>
      )}

      <div onMouseUp={handleMouseUp}>
        <div css={[tw`overflow-hidden h-[152px] px-8 md:(px-16)`]}>
          <div
            css={[tw`flex flex-nowrap w-auto text-center justify-center`]}
            className="modelListItems"
          >
            {/* <div css={[tw`text-center flex pt-4 mx-12 max-h-[200px]`]}> */}
            {items && (
              <AliceCarousel
                mouseTracking
                items={items}
                disableDotsControls
                paddingLeft={10}
                paddingRight={10}
                responsive={responsive}
                controlsStrategy="alternate"
                renderPrevButton={renderPrevButton}
                renderNextButton={renderNextButton}
              />
            )}
          </div>
        </div>
      </div>

      <div css={[tw`bg-white pt-12 overflow-hidden`]}>
        {standardEquipment &&
          standardEquipment.map(
            (se: { items: { name: string; items: any }[] }) => {
              return se.items
                .sort((a, b) => {
                  if (a.name < b.name) {
                    return -1
                  }
                  if (a.name > b.name) {
                    return 1
                  }
                  return 0
                })
                .map((item: { name: string; items: any }) => {
                  return (
                    <div
                      css={[tw`px-6`, tw`lg:(mx-auto px-12)`, tw`2xl:(px-20)`]}
                      key={`standard-equipment-${item.name}`}
                    >
                      <Accordion
                        label={_(item.name, true)}
                        css={[
                          tw`border-t items-start border-gray-500 px-5 pl-0 py-4 mt-0`,
                          tw`md:(mt-0)`,
                        ]}
                      >
                        <ul
                          css={[
                            tw`text-left mr-7 ml-10 grid grid-cols-1`,
                            tw`lg:(grid-cols-2)`,
                          ]}
                        >
                          {Array.isArray(item.items) &&
                            item.items.map(v => (
                              <li
                                css={[
                                  tw`mb-4 list-disc list-outside md:( mr-12)`,
                                ]}
                                key={`standard-equipment-items-${v.title}`}
                              >
                                {parseDisclaimerBlocks(v.title, selection =>
                                  modalDispatch(
                                    toggleDisclaimersModal(selection),
                                  ),
                                )}
                              </li>
                            ))}
                        </ul>
                      </Accordion>
                    </div>
                  )
                })
            },
          )}
      </div>
    </section>
  )
}
export default StandardEquipmentSection
